import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';
import { toast } from 'sonner';
import { ForkKnife, Check, CheckCircle } from '@phosphor-icons/react';

const MealSelection = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [meals, setMeals] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadMealData();
  }, [token]);

  const loadMealData = async () => {
    try {
      const response = await api.get(`/events/meals/select/${token}`);
      setData(response.data);
      setMeals(response.data.current_meals || {});
      if (response.data.meal_selection_completed) {
        setSubmitted(true);
      }
    } catch (error) {
      toast.error('Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };

  const getDatesInRange = (start, end) => {
    const dates = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    while (startDate <= endDate) {
      dates.push(startDate.toISOString().split('T')[0]);
      startDate.setDate(startDate.getDate() + 1);
    }
    return dates;
  };

  const toggleMeal = (date, mealType) => {
    setMeals(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        [mealType]: !prev[date]?.[mealType]
      }
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/events/meals/select/${token}`, { meals });
      toast.success('Meal preferences saved!');
      setSubmitted(true);
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF8EF] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B2560D]"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FBF8EF] flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Invalid Link</h1>
          <p className="text-[#505631]">This meal selection link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FBF8EF] flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md text-center">
          <CheckCircle size={64} className="text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#2B3210] mb-4">Thank You!</h1>
          <p className="text-[#505631] mb-4">Your meal preferences have been saved successfully.</p>
          <div className="bg-[#FBF8EF] p-4 rounded-lg">
            <p className="text-sm text-[#505631]">Event</p>
            <p className="font-medium text-[#2B3210]">{data.event_name}</p>
          </div>
        </div>
      </div>
    );
  }

  const dates = getDatesInRange(data.arrival_date, data.departure_date);

  return (
    <div className="min-h-screen bg-[#FBF8EF] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-[#B2560D] text-white p-6">
            <div className="flex items-center gap-3 mb-2">
              <ForkKnife size={32} />
              <h1 className="text-2xl font-bold">Meal Selection</h1>
            </div>
            <p className="opacity-90">Welcome, {data.participant_name}</p>
          </div>

          {/* Event Info */}
          <div className="p-6 border-b border-[#E5E2D9]">
            <div className="bg-[#FBF8EF] p-4 rounded-lg">
              <p className="text-sm text-[#505631]">Event</p>
              <p className="font-semibold text-[#2B3210] text-lg">{data.event_name}</p>
              <p className="text-sm text-[#505631] mt-2">
                Your stay: {data.arrival_date} to {data.departure_date}
              </p>
            </div>
          </div>

          {/* Meal Selection */}
          <div className="p-6">
            <p className="text-sm text-[#505631] mb-4">
              Select your meals for each day. Each meal is ₦4,000.
            </p>
            
            <div className="space-y-4">
              {dates.map(date => (
                <div key={date} className="border border-[#E5E2D9] rounded-lg p-4">
                  <p className="font-medium text-[#2B3210] mb-3">
                    {new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {['breakfast', 'lunch', 'dinner'].map(mealType => (
                      <button
                        key={mealType}
                        onClick={() => toggleMeal(date, mealType)}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          meals[date]?.[mealType]
                            ? 'border-[#B2560D] bg-orange-50 text-[#B2560D]'
                            : 'border-[#E5E2D9] text-[#505631] hover:border-[#B2560D]'
                        }`}
                      >
                        <div className="flex flex-col items-center gap-1">
                          {meals[date]?.[mealType] && <Check size={18} weight="bold" />}
                          <span className="capitalize text-sm font-medium">{mealType}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="mt-6 bg-[#2B3210] text-white p-4 rounded-lg">
              <p className="text-sm opacity-80">Total Meals Selected</p>
              <p className="text-2xl font-bold">
                {Object.values(meals).reduce((total, day) => {
                  return total + (day.breakfast ? 1 : 0) + (day.lunch ? 1 : 0) + (day.dinner ? 1 : 0);
                }, 0)} meals
              </p>
              <p className="text-sm opacity-80 mt-1">
                ₦{(Object.values(meals).reduce((total, day) => {
                  return total + (day.breakfast ? 1 : 0) + (day.lunch ? 1 : 0) + (day.dinner ? 1 : 0);
                }, 0) * 4000).toLocaleString()}
              </p>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full mt-6 py-3 bg-[#B2560D] hover:bg-[#8B4513] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Save My Meal Preferences'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealSelection;
